// Small shared widgets: stepper, money input, progress bar, sparkline,
// multi-series trend chart, category bar chart, die-size swatch.

import { useEffect, useRef, useState } from "react";

/** Container width via ResizeObserver — charts fill their panel on phones,
 *  capped at their design width on desktop. Text inside the SVGs must not
 *  scale, so we re-plot at the measured width instead of viewBox-stretching. */
export function useMeasuredWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

interface MoneyInputProps {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  width?: number;
}

/** Dollar-amount field. While focused it holds a raw digit string so typing is
 *  never reformatted or clamped under the cursor; on blur it clamps to
 *  [min, max] and shows comma grouping. */
export function MoneyInput({ value, onChange, min = 0, max = Number.MAX_SAFE_INTEGER, width = 110 }: MoneyInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  return (
    <input
      type="text"
      inputMode="numeric"
      value={draft ?? value.toLocaleString("en-US")}
      onFocus={() => setDraft(String(value))}
      onChange={(e) => {
        const digits = e.target.value.replace(/[^0-9]/g, "");
        setDraft(digits);
        if (digits !== "") onChange(clamp(Number(digits)));
      }}
      onBlur={() => {
        onChange(clamp(draft ? Number(draft) : min));
        setDraft(null);
      }}
      style={{ width }}
    />
  );
}

interface StepperProps {
  value: number;
  queued?: number; // pending order value, if any
  min?: number;
  max?: number;
  bigStep?: number;
  onChange: (v: number) => void;
}

/** Numeric stepper; shows the queued order value in amber when it differs. */
export function Stepper({ value, queued, min = 0, max = 9999, bigStep = 5, onChange }: StepperProps) {
  const shown = queued ?? value;
  const set = (v: number) => onChange(Math.max(min, Math.min(max, v)));
  return (
    <span className="stepper">
      <button onClick={() => set(shown - bigStep)}>«</button>
      <button onClick={() => set(shown - 1)}>‹</button>
      <span className={`val${queued !== undefined && queued !== value ? " queued" : ""}`}>{shown}</span>
      <button onClick={() => set(shown + 1)}>›</button>
      <button onClick={() => set(shown + bigStep)}>»</button>
    </span>
  );
}

interface BarProps {
  frac: number; // 0..1
  mark?: number; // 0..1 threshold marker
  amber?: boolean;
  label?: string;
  right?: string;
}

export function Bar({ frac, mark, amber, label, right }: BarProps) {
  return (
    <div>
      {(label || right) && (
        <div className="bar-label">
          <span>{label}</span>
          <span>{right}</span>
        </div>
      )}
      <div className="bar">
        <div className={`fill${amber ? " amber" : ""}`} style={{ width: `${Math.min(100, Math.max(0, frac * 100))}%` }} />
        {mark !== undefined && <div className="mark" style={{ left: `${Math.min(100, Math.max(0, mark * 100))}%` }} />}
      </div>
    </div>
  );
}

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
}

export function Sparkline({ data, width = 140, height = 30, color = "var(--ink)" }: SparklineProps) {
  if (data.length < 2) return <svg width={width} height={height} />;
  const min = Math.min(...data, 0);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pts = data
    .map((v, i) => `${((i / (data.length - 1)) * (width - 2) + 1).toFixed(1)},${(height - 1 - ((v - min) / span) * (height - 2)).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={width} height={height}>
      {min < 0 && (
        <line x1="0" x2={width} y1={height - 1 - ((0 - min) / span) * (height - 2)} y2={height - 1 - ((0 - min) / span) * (height - 2)} stroke="var(--ink-faint)" strokeDasharray="2,3" />
      )}
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.2" />
    </svg>
  );
}

interface Series {
  label: string;
  data: number[];
  color: string;
}

interface MultiLineChartProps {
  series: Series[];
  width?: number;
  height?: number;
}

/** Small multi-series trend line, same raw-SVG technique as Sparkline (one
 *  polyline per series, one shared y-scale) with a text legend below — color
 *  is never the only identity channel. */
export function MultiLineChart({ series, width = 280, height = 70 }: MultiLineChartProps) {
  const [wrapRef, measured] = useMeasuredWidth();
  const w = measured > 0 ? Math.min(width, measured) : width;
  const allVals = series.flatMap((s) => s.data);
  const len = Math.max(0, ...series.map((s) => s.data.length));
  if (allVals.length < 2 || len < 2)
    return (
      <div ref={wrapRef}>
        <svg width={w} height={height} />
      </div>
    );
  const min = Math.min(...allVals, 0);
  const max = Math.max(...allVals);
  const span = max - min || 1;
  const x = (i: number) => (i / (len - 1)) * (w - 2) + 1;
  const y = (v: number) => height - 1 - ((v - min) / span) * (height - 2);
  return (
    <div ref={wrapRef}>
      <svg width={w} height={height}>
        {min < 0 && <line x1={0} x2={w} y1={y(0)} y2={y(0)} stroke="var(--ink-faint)" strokeDasharray="2,3" />}
        {series.map((s) => (
          <polyline
            key={s.label}
            points={s.data.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ")}
            fill="none"
            stroke={s.color}
            strokeWidth="1.2"
          />
        ))}
      </svg>
      <div className="chart-legend">
        {series.map((s) => (
          <span key={s.label} className="chart-legend-item">
            <span className="swatch" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

interface BarChartItem {
  label: string;
  value: number;
}

interface BarChartProps {
  items: BarChartItem[];
  width?: number;
  rowHeight?: number;
  labelWidth?: number;
  formatValue?: (v: number) => string;
  posColor?: string;
  negColor?: string;
}

/** Horizontal bars for a single-snapshot breakdown (no history needed) —
 *  magnitude by bar length, sign by color (ink = inflow, amber = outflow,
 *  matching the existing byCategory table's convention). */
export function BarChart({
  items,
  width = 320,
  rowHeight = 16,
  labelWidth = 130,
  formatValue = (v) => v.toFixed(0),
  posColor = "var(--ink)",
  negColor = "var(--amber)",
}: BarChartProps) {
  const [wrapRef, measured] = useMeasuredWidth();
  const w = measured > 0 ? Math.min(width, measured) : width;
  if (items.length === 0)
    return (
      <div ref={wrapRef}>
        <svg width={w} height={0} />
      </div>
    );
  const max = Math.max(1, ...items.map((it) => Math.abs(it.value)));
  const valueWidth = 76;
  const lw = Math.min(labelWidth, Math.max(70, Math.floor(w * 0.4))); // labels yield first on tight screens
  const barAreaWidth = Math.max(20, w - lw - valueWidth);
  const height = items.length * rowHeight;
  return (
    <div ref={wrapRef}>
      <svg width={w} height={height} fontFamily="var(--font)" fontSize="10">
        {items.map((it, i) => {
          const bw = Math.max(1, (Math.abs(it.value) / max) * barAreaWidth);
          const y = i * rowHeight;
          const color = it.value >= 0 ? posColor : negColor;
          return (
            <g key={it.label}>
              <text x={0} y={y + rowHeight - 5} fill="var(--ink-dim)">
                {it.label}
              </text>
              <rect x={lw} y={y + 2} width={bw} height={rowHeight - 6} fill={color} opacity={0.85} />
              <text x={lw + barAreaWidth + 6} y={y + rowHeight - 5} fill="var(--ink-dim)">
                {formatValue(it.value)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

interface DieRectProps {
  dieAreaMm2: number;
  color?: string;
  ghost?: boolean;
  size?: number;
}

/** Die-size swatch: a square whose side is proportional to sqrt(die area),
 *  filled by market identity color. `ghost` renders a dashed outline only,
 *  for a pre-tapeout estimate rather than a locked size. */
export function DieRect({ dieAreaMm2, color = "var(--ink)", ghost = false, size = 26 }: DieRectProps) {
  const side = Math.max(4, Math.min(size - 4, Math.sqrt(Math.max(0, dieAreaMm2)) * 2));
  const offset = (size - side) / 2;
  return (
    <svg width={size} height={size} style={{ flex: "0 0 auto" }}>
      <rect
        x={offset}
        y={offset}
        width={side}
        height={side}
        fill={ghost ? "none" : color}
        fillOpacity={ghost ? 0 : 0.75}
        stroke={color}
        strokeWidth={1}
        strokeDasharray={ghost ? "2,2" : undefined}
      />
    </svg>
  );
}
